/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useImperativeHandle, forwardRef, useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import { useTheme } from '@mui/material/styles';
import {
    Box,
    Grid,
    FormControl,
    FormHelperText,
    Typography,
    ToggleButtonGroup,
    ToggleButton
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
//
import Iconify from './Iconify';
import { FormProvider, RHFTextField } from './hook-form';
import AssignedToLead from './AssignedToLead';
import DentallyAppointmentSyncNotice from './dentally/DentallyAppointmentSyncNotice';
// store
import { useSelector, dispatch } from '../redux/store';
import { getPracticeTreatmentList } from '../redux/slices/practiceTreatment';
import { saveNewLeadDetail, updateSteps, UpdateAppointment } from '../redux/slices/lead';
// style
import {
    FollowUpTitle,
    gridStyle,
    boxStyle
} from '../styles/RescheduleAppointmentStyle';
import { toggleButtonStyle } from '../styles/Common';
// validation
import { NewAppointmentSchema } from '../validations/validations';

// ---------------------------------------------------------------------------------------------------------------

const RescheduleAppointment = forwardRef(({ handleClose, type, treatmentData, appoinmentID, appoinmentSelectData, pastAppointment }, ref) => {
    const theme = useTheme();
    const isLight = theme.palette.mode === 'light';
    const { practiceUser } = useSelector((state) => state.practiceUser);
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { contactMethod, appointmentType, appointmentMethod } = useSelector((state) => state.schema);
    const getUserList = () => practiceUser?.results?.filter(el => el.user && el.user.first_name && !el.user.is_superuser);
    const [assignToState, setAssignToState] = useState(getUserList()?.filter((el) => appoinmentSelectData?.assignees?.includes(el?.id))?.map((el) => (el)) || []);

    useImperativeHandle(
        ref,
        () => ({
            async submit(status) {
                await handleSubmit((data) => onSubmit(data, status))();
            },
        }),
        [assignToState]
    );

    const getDate = (date) => moment(date, 'DD/MM/YYYY HH:mm:ss');

    const defaultValues = {
        treatment: appoinmentSelectData?.selectedTreatmentData?.treatment || treatmentData && treatmentData.treatment || '',
        treatmentValue: appoinmentSelectData?.selectedTreatmentData?.price || treatmentData && treatmentData.price || '',
        appoinmentDate: new Date(getDate(appoinmentSelectData?.datetime)) || null,
        bookedWith: [],
        practiceContactedVia: appoinmentSelectData?.contacted_via || '',
        preferredContactMethod: appoinmentSelectData?.method || '',
        appointmentType: appoinmentSelectData?.type || '',
        notes: appoinmentSelectData?.note || '',
    };

    const methods = useForm({
        resolver: yupResolver(NewAppointmentSchema(assignToState)),
        defaultValues,
    });

    const {
        control,
        setValue,
        handleSubmit,
        trigger
    } = methods;

    useEffect(() => {
        if (defaultValues.treatment) {
            setValue('treatment', defaultValues.treatment);
            setValue('treatmentValue', defaultValues.treatmentValue);
        }
    }, [defaultValues.treatment]);

    useEffect(() => {
        if (assignToState?.length > 0) {
            trigger('bookedWith');
        }
    }, [assignToState, trigger]);

    const onSubmit = async (data) => {
        if (`${moment(data.appoinmentDate).format('DD-MM-YYYY HH:mm:ss')}.0Z` === `${moment(defaultValues.appoinmentDate).format('DD-MM-YYYY HH:mm:ss')}.0Z` && data.bookedWith === defaultValues.bookedWith && data.practiceContactedVia === defaultValues.practiceContactedVia && data.preferredContactMethod === defaultValues.preferredContactMethod && data.appointmentType === defaultValues.appointmentType) {
            handleClose();
            return;
        }
        if (type === 'RescheduleReminder') {
            const updatedTreatment = {
                treatment: data.treatment,
                custom_price: Number(data.treatmentValue)
            };
            const appointmentDetail = {
                datetime: data.appoinmentDate ? `${moment(data.appoinmentDate).format('DD-MM-YYYY HH:mm:ss')}.0Z` : null,
                method: data.preferredContactMethod || '',
                contacted_via: data.practiceContactedVia || '',
                note: data.notes || '',
                assignees: assignToState?.map((el) => el?.id),
                lead_treatment: appoinmentSelectData?.selectedTreatmentData?.id || treatmentData?.id || '',
                id: appoinmentID,
                lead: treatmentData?.lead,
                type: data?.appointmentType
            };
            dispatch(saveNewLeadDetail({
                startTreatment: {
                    appointmentDetail,
                    updatedTreatment
                }
            }));
            // Pass the appointment's Dentally link so the thunk confirms the
            // provider half of this edit. Undefined for a CRM-only appointment,
            // which keeps that path on its single request.
            dispatch(UpdateAppointment(appointmentDetail, practiceId, handleClose, 'RescheduleReminder', {
                dentally: appoinmentSelectData?.dentally || null,
            }));
        }
    };

    useEffect(() => {
        if (practiceId) {
            dispatch(getPracticeTreatmentList(practiceId));
        }
    }, [practiceId]);

    const updateNewStep = (value) => {
        const attaendence = moment(value).format('YYYY-MM-DD HH:mm:ss') > moment().format('YYYY-MM-DD HH:mm:ss');
        dispatch(updateSteps(attaendence ? 'payment' : 'attendance'));
    };

    return (
        <Box sx={{ width: '100%', pt: '30px' }}>
            <FollowUpTitle sx={{ mb: '30px' }}>Book an appointment</FollowUpTitle>
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <Grid sx={gridStyle}>
                    <Box>
                        <Controller
                            name='appoinmentDate'
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <DateTimePicker
                                    label='Appointment date (Required)'
                                    value={field.value || null}
                                    format={'dd/MM/yyyy HH:mm'}
                                    ampm={false}
                                    disabled={(pastAppointment === 'pastAppointment' || appoinmentSelectData?.Status === 'FAILED_TO_ATTEND') && 'true'}
                                    onChange={(newValue) => {
                                        field.onChange(newValue);
                                        updateNewStep(newValue);
                                    }}
                                    slotProps={{
                                        textField: {
                                            fullWidth: true,
                                            error: !!error,
                                            helperText: error?.message,
                                            onKeyDown: (e) => e.preventDefault()
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>
                    <AssignedToLead staffRole="practitioner" label='Booked with (Required)' assignToState={assignToState} setAssignTo={(val) => { setAssignToState(val); setValue('bookedWith', val); }} />
                </Grid>
                {/* Renders nothing for a CRM-only appointment, or for a practice
                    without an active Dentally integration this user may view. */}
                <DentallyAppointmentSyncNotice
                    dentally={appoinmentSelectData?.dentally}
                    practiceId={practiceId}
                    sx={{ mt: 2 }}
                />
                <Box sx={{ ...boxStyle, gridTemplateColumns: 'repeat(1, 1fr)' }}>
                    <Controller
                        name="preferredContactMethod"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <FormControl>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ m: '0 0 10px' }}
                                >
                                    Appointment method (Required)
                                </Typography>
                                <ToggleButtonGroup
                                    color="primary"
                                    exclusive
                                    value={field.value}
                                    onChange={(e, value) => {
                                        if (value === null) {
                                            field.onChange('');
                                            return;
                                        }
                                        field.onChange(value);
                                    }}
                                    aria-label="Platform"
                                    sx={{ borderRadius: 0, border: 'none', flexWrap: 'wrap' }}
                                    error={error && !field.value?.length}
                                >
                                    {appointmentMethod?.map((options, index) => (
                                        <ToggleButton
                                            key={index}
                                            value={options.value}
                                            sx={toggleButtonStyle(isLight)}
                                        >
                                            <Iconify icon={field.value === options.value ? 'bi:check-circle-fill' : 'material-symbols:circle'} width={20} height={20} sx={{ mr: 1, color: field.value === options.value ? '#00A76F' : '#C4CDD5' }} />{options.label}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                                {error && !field.value?.length &&
                                    <FormHelperText error sx={{ px: 2, textTransform: 'capitalize' }}>
                                        {error?.message}
                                    </FormHelperText>}
                            </FormControl>
                        )}
                    />
                    <Controller
                        name="appointmentType"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <FormControl>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ m: '0 0 10px' }}
                                >
                                    Appointment type (Required)
                                </Typography>
                                <ToggleButtonGroup
                                    color="primary"
                                    exclusive
                                    value={field.value}
                                    onChange={(e, value) => {
                                        if (value === null) {
                                            field.onChange('');
                                            return;
                                        }
                                        field.onChange(value);
                                    }}
                                    aria-label="Platform"
                                    sx={{ borderRadius: 0, border: 'none', flexWrap: 'wrap' }}
                                    error={error && !field.value?.length}
                                >
                                    {appointmentType?.map((options, index) => (
                                        <ToggleButton
                                            key={index}
                                            value={options.value}
                                            sx={toggleButtonStyle(isLight)}
                                        >
                                            <Iconify icon={field.value === options.value ? 'bi:check-circle-fill' : 'material-symbols:circle'} width={20} height={20} sx={{ mr: 1, color: field.value === options.value ? '#00A76F' : '#C4CDD5' }} />{options.label}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                                {error && !field.value?.length &&
                                    <FormHelperText error sx={{ px: 2, textTransform: 'capitalize' }}>
                                        {error?.message}
                                    </FormHelperText>}
                            </FormControl>
                        )}
                    />
                    <Controller
                        name="practiceContactedVia"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <FormControl>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ m: '0 0 10px' }}
                                >
                                    Contact method (Required)
                                </Typography>
                                <ToggleButtonGroup
                                    color="primary"
                                    exclusive
                                    value={field.value}
                                    onChange={(e, value) => {
                                        if (value === null) {
                                            field.onChange('');
                                            return;
                                        }
                                        field.onChange(value);
                                    }}
                                    aria-label="Platform"
                                    sx={{ borderRadius: 0, border: 'none', flexWrap: 'wrap' }}
                                    error={error && !field.value?.length}
                                >
                                    {contactMethod?.map((options, index) => (
                                        <ToggleButton
                                            key={index}
                                            value={options.value}
                                            sx={toggleButtonStyle(isLight)}
                                        >
                                            <Iconify icon={field.value === options.value ? 'bi:check-circle-fill' : 'material-symbols:circle'} width={20} height={20} sx={{ mr: 1, color: field.value === options.value ? '#00A76F' : '#C4CDD5' }} />{options.label}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                                {error && !field.value?.length &&
                                    <FormHelperText error sx={{ px: 2, textTransform: 'capitalize' }}>
                                        {error?.message}
                                    </FormHelperText>}
                            </FormControl>
                        )}
                    />
                </Box>
                <Box sx={{ mt: 3 }}>
                    <RHFTextField
                        sx={{ width: { xs: '100%', md: '100%' } }}
                        name="notes" multiline rows={4}
                        label="Appointment notes"
                        placeholder="Add notes here"
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>
            </FormProvider>
        </Box>
    );
});

RescheduleAppointment.propTypes = {
    handleClose: PropTypes.func,
    type: PropTypes.any,
    treatmentData: PropTypes.any,
    appoinmentID: PropTypes.any,
    appoinmentSelectData: PropTypes.any,
    pastAppointment: PropTypes.any,
};

export default RescheduleAppointment;